import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  AuthTokensSchema,
  type AdminLoginInput,
  type AuthTokens,
  type AuthUser,
  type Capability,
  type OwnerLoginInput,
  can,
  capabilitiesForRole,
} from "@steelbill/types";
import { api, setSessionExpiredHandler } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import { endpoints } from "@/lib/endpoints";
import {
  clearSession,
  getStaySignedIn,
  getStoredSession,
  saveSession,
  setStaySignedIn,
} from "@/lib/token-storage";
import { routes } from "@/routes/paths";

/**
 * The auth context (spec §2.1, §11.1).
 *
 * Holds the signed-in user, the two sign-in paths (§2 — there are exactly two
 * humans), and the capability predicate every guard and every disabled button
 * in the app resolves through.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `can()` COMES FROM `@steelbill/types`. IT IS NOT REIMPLEMENTED HERE.
 *
 * §2.1: "Never write `if (user.role === 'ADMIN')` in a route handler or a
 * component. ... The client uses the same source. A `useCan('invoice.cancel')`
 * hook imported from `packages/types` drives disabled states and hidden nav
 * items, so UI and API can never disagree."
 *
 * The server calls `requireCap(...)` → `can(role, capability)`; this file calls
 * `can(role, capability)`. Same function, same map, same file
 * (`packages/types/src/permissions.ts`). A role string never appears in a
 * component in this app, and a capability typo is a compile error because
 * `Capability` is a union.
 *
 * And it is still only UX. §2.1: "Client-side checks are UX only — hiding a
 * button is not security." The server refuses regardless, and audits the
 * refusal.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface AuthState {
  /** The signed-in user, or null. */
  user: AuthUser | null;
  /** True once the stored session has been read — see `ProtectedRoute`. */
  ready: boolean;
  signedIn: boolean;
  /** §11.1 — defaults to ON. */
  staySignedIn: boolean;
  setStaySignedIn: (stay: boolean) => void;
  /** Every capability the current role holds. For a Settings/user screen. */
  capabilities: readonly Capability[];
  /** §2.1 — the ONE authorisation predicate on the client. */
  can: (capability: Capability) => boolean;
  loginAsOwner: (input: OwnerLoginInput, stay?: boolean) => Promise<AuthUser>;
  loginAsAdmin: (input: AdminLoginInput, stay?: boolean) => Promise<AuthUser>;
  signOut: () => void;
}

const AuthContext = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /*
   * Read synchronously on first render, not in an effect. An effect would give
   * one frame in which `user` is null, and `ProtectedRoute` would bounce an
   * already-signed-in owner to the login screen and back on every cold start —
   * a flash of the PIN pad that §11.1 exists to avoid.
   */
  const [user, setUser] = React.useState<AuthUser | null>(() => getStoredSession()?.user ?? null);
  const [stay, setStayState] = React.useState<boolean>(() => getStaySignedIn());

  const applyTokens = React.useCallback((tokens: AuthTokens, keepSignedIn: boolean): AuthUser => {
    saveSession(tokens, keepSignedIn);
    setUser(tokens.user);
    return tokens.user;
  }, []);

  const signOut = React.useCallback(() => {
    /*
     * TELL THE SERVER FIRST, AND DO NOT WAIT FOR IT (§13, §14.3).
     *
     * The owner asked for "a full audit window — who logged in when, what
     * happened", and until 2026-08-17 there was no sign-out to record at all:
     * the log showed three people arriving and nobody ever leaving. `POST
     * /auth/logout` writes that row.
     *
     * FIRE AND FORGET, ON PURPOSE, AND IN THIS ORDER. The token has to still be
     * attached for the row to be attributable, so the call goes out before
     * `clearSession()`. But sign-out must never depend on the network: a failed
     * request, an expired access token or a dead VPS must still get the person
     * off this device — that is what "sign out" means on a shared office
     * desktop, and blocking it on a promise would be the one bug that leaves
     * somebody signed in. `.catch` swallows the failure deliberately; the audit
     * row is the nice-to-have here and the local wipe is the guarantee.
     */
    void api.post(endpoints.logout, undefined).catch(() => undefined);

    clearSession();
    setUser(null);
    /*
     * Wipe the cache. Otherwise the next person to sign in on the office
     * desktop sees the previous user's invoice list painted from cache before
     * their own request returns.
     */
    queryClient.clear();
  }, [queryClient]);

  /*
   * `lib/api.ts` calls this when a refresh fails. Navigating through the router
   * (rather than `location.assign`) keeps the app mounted, so the toast that
   * explains what happened survives the transition — and `state.from` lets the
   * login screen send him back where he was.
   */
  React.useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      queryClient.clear();
      navigate(routes.login, {
        replace: true,
        state: { from: window.location.pathname, reason: "expired" },
      });
    });
    return () => {
      setSessionExpiredHandler(null);
    };
  }, [navigate, queryClient]);

  const login = React.useCallback(
    async (path: string, body: unknown, keepSignedIn: boolean): Promise<AuthUser> => {
      // `anonymous` — a 401 here means "wrong PIN", and must not trigger the
      // refresh-and-retry path in `lib/api.ts`.
      const payload = await api.post<unknown>(path, body, { anonymous: true });

      // Validated with the SHARED schema (§13), not cast. A response that does
      // not match must fail loudly here rather than be written to storage and
      // rediscovered three screens later as `user.role === undefined`.
      const parsed = AuthTokensSchema.safeParse(payload);
      if (!parsed.success) {
        throw new ApiError(500, "Unexpected sign-in response.", { endpoint: `POST ${path}` });
      }
      return applyTokens(parsed.data, keepSignedIn);
    },
    [applyTokens],
  );

  const value = React.useMemo<AuthState>(() => {
    const role = user?.role ?? null;
    return {
      user,
      ready: true,
      signedIn: user !== null,
      staySignedIn: stay,
      setStaySignedIn: (next: boolean) => {
        setStayState(next);
        setStaySignedIn(next);
      },
      capabilities: role === null ? [] : capabilitiesForRole(role),
      /** Deny by default when signed out — `can()` itself is deny-by-default too. */
      can: (capability: Capability) => (role === null ? false : can(role, capability)),
      loginAsOwner: (input, keepSignedIn = stay) =>
        login(endpoints.ownerLogin, input, keepSignedIn),
      loginAsAdmin: (input, keepSignedIn = stay) =>
        login(endpoints.adminLogin, input, keepSignedIn),
      signOut,
    };
  }, [user, stay, login, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = React.useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used inside <AuthProvider>.");
  }
  return context;
}

/**
 * `useCan("invoice.cancel")` — §2.1's named hook.
 *
 * Use it for disabled states and hidden nav items. The capability string is
 * checked at compile time against the union in `packages/types`, so a typo
 * cannot silently resolve to `false` and hide a button forever.
 */
export function useCan(capability: Capability): boolean {
  return useAuth().can(capability);
}
